import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import { generateConciseTitle } from '@/utils/languageUtils';
import { useLocalizations } from '@/hooks/useLocalizations';

export interface ChatSession {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string | null;
  intent_category: string | null;
  extracted_location: string | null;
  project_id: string | null;
}

interface UseChatSessionsOptions {
  projectId?: string | null;
}

export interface GroupedSessions {
  today: ChatSession[];
  yesterday: ChatSession[];
  lastWeek: ChatSession[];
  older: ChatSession[];
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export interface SessionLoadResult {
  messages: Message[];
  isCompleted: boolean;
  metadata: Record<string, unknown> | null;
}

export function useChatSessions(options: UseChatSessionsOptions = {}) {
  const { projectId } = options;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { lCommon } = useLocalizations();

  // Fetch sessions for the current user, optionally filtered by project
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['chat-sessions', user?.id, projectId],
    queryFn: async () => {
      if (!user?.id) return [];

      let query = supabase
        .from('ai_chat_sessions')
        .select('id, session_key, created_at, updated_at, intent_category, extracted_location, metadata, user_id, project_id')
        // Explicitly scope to the authenticated user's sessions.
        // Relying on RLS alone can still allow guest rows (user_id IS NULL)
        // depending on policy, and those can break title parsing.
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(100);

      // Filter by project if specified
      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error fetching chat sessions', { error });
        return [];
      }

      // Map to ChatSession interface, extracting title from metadata or generating from session_key
      return (data || []).map((session): ChatSession => {
        const metadata = session.metadata as { title?: string } | null;

        // Defensive: a single malformed session_key should never break History.
        let title: string | null = metadata?.title ?? null;
        if (!title) {
          try {
            title = generateTitleFromKey(session.session_key);
          } catch {
            title = lCommon('chat_sessions.new_conversation.title', { defaultValue: 'New conversation' });
          }
        }

        return {
          id: session.id,
          title,
          created_at: session.created_at || '',
          updated_at: session.updated_at,
          intent_category: session.intent_category,
          extracted_location: session.extracted_location,
          project_id: session.project_id,
        };
      });
    },
    enabled: !!user?.id,
  });

  // Real-time subscription for live updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`chat-sessions-realtime-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ai_chat_sessions',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          logger.info('Chat session realtime update', { event: payload.eventType });
          queryClient.invalidateQueries({ queryKey: ['chat-sessions', user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  // Group sessions by time period
  const groupedSessions = useMemo((): GroupedSessions => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    return sessions.reduce<GroupedSessions>(
      (groups, session) => {
        const sessionDate = new Date(session.updated_at || session.created_at);
        
        if (sessionDate >= today) {
          groups.today.push(session);
        } else if (sessionDate >= yesterday) {
          groups.yesterday.push(session);
        } else if (sessionDate >= lastWeek) {
          groups.lastWeek.push(session);
        } else {
          groups.older.push(session);
        }
        
        return groups;
      },
      { today: [], yesterday: [], lastWeek: [], older: [] }
    );
  }, [sessions]);

  // Create new session
  // For anonymous users, generate a local session ID (no DB persistence)
  // For authenticated users, persist to database
  const createSessionMutation = useMutation({
    mutationFn: async (newProjectId?: string | null) => {
      // For anonymous users, skip database and return a local UUID
      if (!user?.id) {
        const localSessionId = `local_${crypto.randomUUID()}`;
        logger.info('Created local session for anonymous user', { localSessionId });
        return localSessionId;
      }
      
      const sessionKey = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      
      const { data, error } = await supabase
        .from('ai_chat_sessions')
        .insert({
          session_key: sessionKey,
          messages: [],
          metadata: { title: null },
          user_id: user.id,
          project_id: newProjectId || projectId || null,
        })
        .select()
        .single();

      if (error) {
        logger.error('Error creating chat session', { error });
        throw error;
      }

      return data.id;
    },
    onSuccess: () => {
      // Only invalidate queries for authenticated users (they have DB sessions)
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
        if (projectId) {
          queryClient.invalidateQueries({ queryKey: ['chat-projects'] });
        }
      }
    },
  });

  const createSession = useCallback(async (newProjectId?: string | null): Promise<string | null> => {
    try {
      return await createSessionMutation.mutateAsync(newProjectId);
    } catch {
      return null;
    }
  }, [createSessionMutation]);

  // Load session messages with title backfill
  // Returns messages AND completion status to prevent reprocessing completed chats
  // For local sessions (anonymous users), return empty - they don't have DB persistence
  const loadSession = useCallback(async (sessionId: string): Promise<SessionLoadResult> => {
    logger.info('[useChatSessions] Loading session', { sessionId });
    
    const isLocalSession = sessionId.startsWith('local_');
    
    // For local sessions, query by session_key (backend persists with local_ prefix)
    // For UUID sessions, query by id
    let query;
    if (isLocalSession) {
      query = supabase
        .from('ai_chat_sessions')
        .select('messages, metadata')
        .eq('session_key', sessionId)
        .maybeSingle();
    } else {
      query = supabase
        .from('ai_chat_sessions')
        .select('messages, metadata')
        .eq('id', sessionId)
        .maybeSingle();
    }
    
    const { data, error } = await query;

    if (error) {
      logger.error('[useChatSessions] Error loading session', { error, sessionId });
      return { messages: [], isCompleted: false, metadata: null };
    }

    if (!data) {
      logger.warn('[useChatSessions] No session found', { sessionId });
      return { messages: [], isCompleted: false, metadata: null };
    }

    // Parse messages from JSON
    const messages = data.messages;
    const parsedMessages: Message[] = Array.isArray(messages) ? messages as Message[] : [];
    
    // Parse metadata to check completion status
    const metadata = data.metadata as { 
      title?: string; 
      stream_completed?: boolean;
      partial?: boolean;
    } | null;
    
    // Session is completed if:
    // - stream_completed is explicitly true, OR
    // - partial is explicitly false, OR
    // - last message is from assistant (completed response)
    const lastMessage = parsedMessages[parsedMessages.length - 1];
    const isCompleted = metadata?.stream_completed === true || 
                        metadata?.partial === false ||
                        (parsedMessages.length > 0 && lastMessage?.role === 'assistant');
    
    logger.info('[useChatSessions] Session completion status', { 
      sessionId, 
      isCompleted,
      streamCompleted: metadata?.stream_completed,
      partial: metadata?.partial,
      lastMessageRole: lastMessage?.role
    });
    
    // Backfill title if missing and we have messages
    if (!metadata?.title && parsedMessages.length > 0) {
      const firstUserMessage = parsedMessages.find(m => m.role === 'user');
      if (firstUserMessage) {
        const title = generateConciseTitle(firstUserMessage.content);
        logger.info('[useChatSessions] Backfilling missing title', { sessionId, title });
        
        // Update title in background (don't block return)
        supabase
          .from('ai_chat_sessions')
          .update({ 
            metadata: { ...(metadata || {}), title },
            updated_at: new Date().toISOString() 
          })
          .eq('id', sessionId)
          .then(({ error: updateError }) => {
            if (updateError) {
              logger.error('[useChatSessions] Error backfilling title', { error: updateError });
            } else {
              queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
            }
          });
      }
    }
    
    logger.info('[useChatSessions] Session loaded', { sessionId, messageCount: parsedMessages.length, isCompleted });
    return { messages: parsedMessages, isCompleted, metadata: metadata as Record<string, unknown> | null };
  }, [queryClient]);

  // Delete session
  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      // Local sessions don't exist in DB
      if (sessionId.startsWith('local_')) {
        logger.info('[useChatSessions] Skipping delete for local session', { sessionId });
        return;
      }
      
      const { error } = await supabase
        .from('ai_chat_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) {
        logger.error('Error deleting session', { error });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
    },
  });

  const deleteSession = useCallback(async (sessionId: string) => {
    await deleteSessionMutation.mutateAsync(sessionId);
  }, [deleteSessionMutation]);

  // Search sessions
  const searchSessions = useCallback((query: string): GroupedSessions => {
    if (!query.trim()) return groupedSessions;

    const lowerQuery = query.toLowerCase();
    const filterSessions = (arr: ChatSession[]) =>
      arr.filter(s => s.title?.toLowerCase().includes(lowerQuery));

    return {
      today: filterSessions(groupedSessions.today),
      yesterday: filterSessions(groupedSessions.yesterday),
      lastWeek: filterSessions(groupedSessions.lastWeek),
      older: filterSessions(groupedSessions.older),
    };
  }, [groupedSessions]);

  // Update session title (merge with existing metadata)
  // Skip for local sessions (anonymous users)
  const updateSessionTitle = useCallback(async (sessionId: string, title: string) => {
    // Local sessions don't have DB persistence
    if (sessionId.startsWith('local_')) {
      logger.info('[useChatSessions] Skipping title update for local session', { sessionId, title });
      return;
    }
    
    // First fetch existing metadata to merge
    const { data: existing } = await supabase
      .from('ai_chat_sessions')
      .select('metadata')
      .eq('id', sessionId)
      .maybeSingle();
    
    const existingMetadata = (existing?.metadata as Record<string, unknown>) || {};
    
    const { error } = await supabase
      .from('ai_chat_sessions')
      .update({ 
        metadata: { ...existingMetadata, title },
        updated_at: new Date().toISOString() 
      })
      .eq('id', sessionId);

    if (error) {
      logger.error('Error updating session title', { error });
    } else {
      queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
    }
  }, [queryClient]);

  // Claim a guest session (user_id: null) when a user signs in
  // For local sessions, we can't claim them (no DB record), so return false
  const claimGuestSession = useCallback(async (sessionId: string, userId: string): Promise<boolean> => {
    // Local sessions can't be claimed (no DB record)
    if (sessionId.startsWith('local_')) {
      logger.info('[useChatSessions] Cannot claim local session', { sessionId, userId });
      return false;
    }
    
    logger.info('[useChatSessions] Claiming guest session for user', { sessionId, userId });
    
    const { error } = await supabase
      .from('ai_chat_sessions')
      .update({ user_id: userId })
      .eq('id', sessionId)
      .is('user_id', null); // Only claim if it's still a guest session
    
    if (error) {
      logger.error('[useChatSessions] Error claiming guest session', { error, sessionId });
      return false;
    }
    
    logger.info('[useChatSessions] Successfully claimed guest session', { sessionId, userId });
    queryClient.invalidateQueries({ queryKey: ['chat-sessions', userId] });
    return true;
  }, [queryClient]);

  return {
    sessions,
    groupedSessions,
    isLoading,
    createSession,
    loadSession,
    deleteSession,
    searchSessions,
    updateSessionTitle,
    claimGuestSession,
  };
}

// Helper to generate a readable title from session key
// Note: This uses a simple date format as it's for internal session naming
// and doesn't have access to the locale context (utility function, not a hook)
function generateTitleFromKey(key: string): string {
  // Non-standard keys exist (e.g. benchmark_*). Avoid throwing.
  if (key.startsWith('benchmark_')) return 'Benchmark chat';

  const timestampPart = key.split('_')[1];
  const timestamp = timestampPart ? Number(timestampPart) : NaN;
  if (Number.isFinite(timestamp)) {
    const date = new Date(timestamp);
    if (Number.isFinite(date.getTime())) {
      // Use ISO format for consistency - this is internal session naming
      return `Chat ${date.toISOString().split('T')[0]}`;
    }
  }

  return 'New conversation';
}
